import { useCallback, useEffect, useState } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { useRouter } from "expo-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { step4Schema, type Step4Values } from "../schemas/preVettingSchema";
import { usePreVettingDraft } from "./usePreVettingDraft";
import { useDebouncedDraftSave } from "./useDebouncedDraftSave";
import { useRestoredDocumentFile } from "@/hooks/useRestoredDocumentFile";
import { preVettingApi } from "@/lib/api/preVetting";
import type { FileUploadState, SelectedFile } from "@/components/ui/FileUpload";
import type { PreVettingDraft } from "@chinese-vendor/schemas";
import type { TranslationKeys } from "@chinese-vendor/i18n";

export function usePreVettingStep4Form() {
  const router = useRouter();
  const { draftQuery, saveDraftMutation } = usePreVettingDraft();
  const scheduleSave = useDebouncedDraftSave(saveDraftMutation.mutateAsync);

  const form = useForm<Step4Values>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(step4Schema as any) as unknown as Resolver<Step4Values>,
    defaultValues: {
      payment_channels: ["bank"],
      bank: { bank_settlement_currency: "CNY" },
      alipay: { alipay_settlement_currency: "CNY" },
      wechat_pay: { wechatpay_settlement_currency: "CNY" },
    },
    mode: "onBlur",
  });

  // Populate form once draft has loaded
  useEffect(() => {
    if (draftQuery.isSuccess && draftQuery.data?.step4) {
      const current = form.getValues();
      form.reset({
        ...current,
        ...(draftQuery.data.step4 as Partial<Step4Values>),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftQuery.isSuccess]);

  const onFieldBlur = useCallback(() => {
    scheduleSave({ step4: form.getValues() } as unknown as PreVettingDraft);
  }, [scheduleSave, form]);

  // ── WeChat Pay QR upload ──────────────────────────────────────────────────
  const [wechatQrFile, setWechatQrFile] = useState<SelectedFile | null>(null);
  const [wechatQrUploadState, setWechatQrUploadState] = useState<FileUploadState>("idle");
  const [wechatQrUploadError, setWechatQrUploadError] = useState<TranslationKeys | undefined>(
    undefined
  );

  const toggleChannel = useCallback(
    (channel: "bank" | "alipay" | "wechat_pay") => {
      const current = form.getValues("payment_channels") ?? [];
      const isRemoving = current.includes(channel);
      const updated = isRemoving ? current.filter((c) => c !== channel) : [...current, channel];
      form.setValue("payment_channels", updated, { shouldValidate: true });
      if (isRemoving) {
        form.resetField(channel);
        if (channel === "wechat_pay") {
          setWechatQrFile(null);
          setWechatQrUploadState("idle");
          setWechatQrUploadError(undefined);
        }
      }
      onFieldBlur();
    },
    [form, onFieldBlur]
  );
  // Restore file preview from saved S3 key on draft load
  const { file: restoredWechatQr, uploadState: restoredWechatQrState } = useRestoredDocumentFile(
    draftQuery.isSuccess ? (draftQuery.data?.step4?.wechat_pay?.wechatpay_qr_string ?? null) : null
  );
  useEffect(() => {
    if (restoredWechatQr && wechatQrUploadState === "idle" && !wechatQrFile) {
      setWechatQrFile(restoredWechatQr);
      setWechatQrUploadState(restoredWechatQrState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredWechatQr]);
  const handleWechatQrFile = useCallback(
    async (file: SelectedFile) => {
      setWechatQrFile(file);
      setWechatQrUploadState("uploading");
      setWechatQrUploadError(undefined);
      try {
        const key = await preVettingApi.uploadDocument(file);
        form.setValue("wechat_pay.wechatpay_qr_string", key as never);
        onFieldBlur();
        setWechatQrUploadState("success");
      } catch {
        setWechatQrUploadState("error");
        setWechatQrUploadError("fileUpload.uploadFailed");
      }
    },
    [form, onFieldBlur]
  );

  const handleWechatQrRemove = useCallback(() => {
    setWechatQrFile(null);
    setWechatQrUploadState("idle");
    setWechatQrUploadError(undefined);
    form.setValue("wechat_pay.wechatpay_qr_string", null as never);
  }, [form]);

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  const handleNext = form.handleSubmit(async (values) => {
    await saveDraftMutation.mutateAsync({ step4: values } as unknown as PreVettingDraft);
    router.push("/(vendor)/pre-vetting/step-5");
  });

  return {
    form,
    onFieldBlur,
    handleNext,
    handleBack,
    toggleChannel,
    wechatQrFile,
    wechatQrUploadState,
    wechatQrUploadError,
    handleWechatQrFile,
    handleWechatQrRemove,
  };
}
